import { useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { cn, useTranslation } from "@/packages/utils";
import { useBoardScroll } from "./BoardContext";
import { useBoardMinimap } from "./useBoardMinimap";
import type { BoardMinimapProps } from "./types";

const MAX_CARDS = 8;

export function BoardMinimap({ columns }: BoardMinimapProps) {
  const { t } = useTranslation(["common"]);
  const { wrapperRef } = useBoardScroll();
  const {
    visible,
    viewportLeft,
    viewportWidth,
    onMinimapClick,
    onViewportDrag,
  } = useBoardMinimap();

  const trackRef = useRef<HTMLDivElement>(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);

  const getFractionFromEvent = useCallback((clientX: number) => {
    const track = trackRef.current;
    if (!track) return 0;
    const rect = track.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  }, []);

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      if (draggingRef.current) return;
      e.stopPropagation();
      onMinimapClick(getFractionFromEvent(e.clientX));
    },
    [onMinimapClick, getFractionFromEvent],
  );

  const handleTrackKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const track = trackRef.current;
      if (!track) return;
      const step = 0.1;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onMinimapClick(Math.max(0, viewportLeft - step));
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        onMinimapClick(Math.min(1, viewportLeft + viewportWidth + step));
      }
    },
    [onMinimapClick, viewportLeft, viewportWidth],
  );

  const startDrag = useCallback((clientX: number) => {
    draggingRef.current = true;
    startXRef.current = clientX;
  }, []);

  const moveDrag = useCallback(
    (clientX: number) => {
      if (!draggingRef.current) return;
      const track = trackRef.current;
      if (!track) return;
      const deltaX = clientX - startXRef.current;
      startXRef.current = clientX;
      const deltaFraction = deltaX / track.getBoundingClientRect().width;
      onViewportDrag(deltaFraction);
    },
    [onViewportDrag],
  );

  const endDrag = useCallback(() => {
    requestAnimationFrame(() => {
      draggingRef.current = false;
    });
  }, []);

  const handleViewportMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();
      startDrag(e.clientX);

      const onMove = (ev: MouseEvent) => moveDrag(ev.clientX);
      const onUp = () => {
        endDrag();
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onUp);
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    },
    [startDrag, moveDrag, endDrag],
  );

  const handleViewportTouchStart = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      const touch = e.touches[0];
      startDrag(touch.clientX);
    },
    [startDrag],
  );

  const handleViewportTouchMove = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      const touch = e.touches[0];
      moveDrag(touch.clientX);
    },
    [moveDrag],
  );

  const handleViewportTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      e.stopPropagation();
      endDrag();
    },
    [endDrag],
  );

  if (!wrapperRef.current) return null;

  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 8 }}
          transition={{ duration: 0.2 }}
          className="fixed bottom-7 right-22 z-30"
          style={{ width: 120, maxWidth: "30%" }}
        >
          <div
            ref={trackRef}
            role="slider"
            tabIndex={0}
            aria-label={t("ui.board.minimap")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(viewportLeft * 100)}
            onClick={handleTrackClick}
            onKeyDown={handleTrackKeyDown}
            className="relative h-10 rounded-lg bg-surface/90 backdrop-blur-sm border border-border/30 shadow-sm cursor-pointer flex items-stretch gap-0.5 px-1 py-1"
          >
            {/* Jira-style columns: fixed slot count, filled by card density */}
            {columns.map((col) => {
              const filledCount = Math.min(col.cardCount, MAX_CARDS);

              return (
                <div
                  key={col.id}
                  aria-hidden="true"
                  className="flex-1 flex h-full flex-col gap-0.5 rounded bg-muted/40 p-0.5 min-w-0"
                >
                  {Array.from({ length: MAX_CARDS }).map((_, i) => (
                    <div
                      key={`${col.id}-${i}`}
                      className={cn(
                        "w-full flex-1 min-h-0 shrink-0 rounded-sm",
                        i < filledCount
                          ? "bg-muted-foreground/20"
                          : "bg-transparent",
                      )}
                    />
                  ))}
                </div>
              );
            })}

            {/* Viewport indicator with accent border */}
            {/* biome-ignore lint/a11y/noStaticElementInteractions: drag handle for minimap viewport */}
            <div
              role="presentation"
              className="absolute top-0 bottom-0 border-2 border-accent rounded-md bg-accent/5 cursor-grab active:cursor-grabbing"
              style={{
                left: `${viewportLeft * 100}%`,
                width: `${viewportWidth * 100}%`,
              }}
              onMouseDown={handleViewportMouseDown}
              onTouchStart={handleViewportTouchStart}
              onTouchMove={handleViewportTouchMove}
              onTouchEnd={handleViewportTouchEnd}
            />
          </div>
        </motion.div>
      )}
    </AnimatePresence>,
    wrapperRef.current,
  );
}
